import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../state/store";
import {
  resetConsents,
  setConsentsAppType,
  setConsentsError,
  setConsentsLoading,
  setConsentsMarket,
  setDuplicateConsentId,
  setIsDuplicate,
  setSelectedApp
} from "../../state/ducks";

import { alignItems, routePaths, strCode } from "../../consts";
import { consentsService, log, notificationService, t } from "../../services";
import { consentUtil, responseUtil } from "../../utils";
import { withSection } from "../../hoc";

import { Consents, DuplicateDestinationModal } from "../../containers";
import {
  AppManagementBreadcrumbs,
  FlexContainers,
  Heading5,
  PageContainer,
  RefreshButton,
  SmallButton,
  WrapperContainer
} from "../../components";

const ConsentManagementPage = () => {
  const consentsData = useSelector((store) => store.consents?.consents);
  const loading = useSelector((store) => store.consents?.loading);
  const refresh = useSelector((store) => store.consents?.refresh);

  const consentsMarket = useSelector((store) => store.consents?.market);
  const allMarkets = useSelector((store) => store.markets?.markets);

  const apps = useSelector((store) => store.apps?.apps);
  const currentAppType = useSelector((store) => store.consents?.appType);

  const [duplicateDestinationModalActive, setDuplicateDestinationModalActive] = useState(false);

  const consents = useMemo(() => consentUtil.getConsents(consentsData), [consentsData]);
  const drafts = useMemo(() => consentUtil.getDrafts(consentsData), [consentsData]);
  const marketName = useMemo(
    () => allMarkets.find((market) => market.market === consentsMarket)?.name || "",
    [allMarkets, consentsMarket]
  );

  const { appName } = useParams();

  const history = useHistory();

  const fetchConsents = useCallback(async () => {
    dispatch(setConsentsError(null));

    if (loading) {
      return;
    }

    dispatch(setConsentsLoading(true));

    try {
      dispatch(resetConsents());
      await consentsService.fetchConsents();
    } catch (err) {
      dispatch(setConsentsError(err));

      log.error("ConsentManagementPage: Failed to fetch consents", err);

      notificationService.showError({
        title: t(strCode.FETCH_CONSENTS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setConsentsLoading(false));
    }
  }, [refresh]);

  useEffect(() => {
    fetchConsents();
  }, [refresh]);

  const onCreateConsent = () => {
    history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENTS_CONSENT_ELEMENTS}`);
  };

  const onDuplicateConsent = (id, sameLocation) => {
    dispatch(setDuplicateConsentId(id));
    if (sameLocation) {
      duplicateConsent(currentAppType, consentsMarket);
    } else {
      setDuplicateDestinationModalActive(true);
    }
  };

  const duplicateConsent = (appType, market) => {
    const id = getState().consents.duplicateConsentId;

    dispatch(setConsentsAppType(appType));
    dispatch(setConsentsMarket(market));
    dispatch(setIsDuplicate(true));

    const app = apps.find((x) => x.appType === appType);
    const appName = app?.name ?? appType;
    history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENTS_CONSENT_ELEMENTS}/${id}`);
    setDuplicateDestinationModalActive(false);
    dispatch(setDuplicateConsentId(null));
    dispatch(setSelectedApp(app));
  };

  const onConsentsConfirm = () => {
    history.push(routePaths.CREATE_APP);
  };

  const onDeleteDraft = async (id) => {
    dispatch(setConsentsLoading(true));

    try {
      const res = await consentsService.deleteDraftConsent(id);

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.UPDATE_CONSENT_SUCCESS),
          message: t(strCode.CONSENT_UPDATE_SUCCESS)
        });

        dispatch(setConsentsLoading(false));
        await fetchConsents();
      }
    } catch (err) {
      log.error("ConsentManagementPage: Failed to delete consent", err);

      notificationService.showError({
        title: t(strCode.DELETE_CONSENT_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      dispatch(setConsentsLoading(false));
    }
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%"
        height="100%">
        <AppManagementBreadcrumbs />

        <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%">
          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16}>
            <Heading5>{`${marketName} ${t(strCode.CONSENT_MANAGEMENT_TITLE)}`}</Heading5>
            <RefreshButton isLoading={loading} handleRefresh={() => fetchConsents()} />
          </WrapperContainer>
        </WrapperContainer>

        <Consents
          consentsData={consents}
          draftConsentsData={drafts}
          onDuplicateConsent={onDuplicateConsent}
          onDeleteDraft={onDeleteDraft}
          onCreateConsent={onCreateConsent}
        />

        <DuplicateDestinationModal
          active={duplicateDestinationModalActive}
          setActive={setDuplicateDestinationModalActive}
          apps={apps}
          onSubmit={duplicateConsent}
        />

        {!loading && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnEnd}
            height="100%"
            width="100%"
            alignItems="flex-end"
            mt={24}
            pb={20}>
            <SmallButton onClick={onConsentsConfirm}>{t(strCode.OK_TITLE)}</SmallButton>
          </WrapperContainer>
        )}
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(ConsentManagementPage);
